<template>
  <div class="flex items-center my-5 cursor-pointer" @click="navigateBack">
    <ChevronLeftIcon class="text-brand-green w-5 h-5 mr-1" />
    <p class="text-sm">Back to documents</p>
  </div>

  <template v-if="isDocumentTemplateLoading">
    <Loading />
  </template>
  <template v-else>
    <div
      class="flex flex-row justify-between bg-white rounded-lg w-full max-w-screen-xl py-2 px-5 my-3"
    >
      <div class="flex flex-col py-3">
        <h5 class="text-lg">Document name</h5>
        <SingleLineInput
          :value="name"
          @input="name = $event.target.value"
          placeholder="Rental disclosures 2023"
        ></SingleLineInput>
        <p class="text-sm text-brand-gray">
          Give it a helpful name (ex. applicant disclosures, 2023 lease, payment authorization, etc)
        </p>
      </div>
      <div class="flex items-center w-2/12">
        <PrimaryButton
          @click="save"
          :disabled="isDocumentTemplateLoading"
          :isLoading="isDocumentTemplateLoading"
          >Save</PrimaryButton
        >
      </div>
    </div>

    <div class="flex flex-row min-w-screen-lg max-w-screen-xl w-full py-2">
      <!-- START LEFT PANEL -->
      <div
        class="hidden md:flex flex-col w-1/3 bg-white rounded py-5 panel mr-5 h-screen relative py-5 px-3"
      >
        <div class="flex items-center justify-between py-3 px-5">
          <div
            class="flex items-center py-1 px-3 cursor-pointer"
            :class="[sidePanelTab === 'input' ? 'border-b-2 border-brand-green' : '']"
            @click="setSidePanelTab('input')"
          >
            <p :class="[sidePanelTab === 'input' ? 'text-brand-green' : '']">Input Fields</p>
          </div>

          <div
            class="flex items-center py-1 px-3 cursor-pointer"
            :class="[sidePanelTab === 'mappable' ? 'border-b-2 border-brand-green' : '']"
            @click="setSidePanelTab('mappable')"
          >
            <p :class="[sidePanelTab === 'mappable' ? 'text-brand-green' : '']">Mappable Fields</p>
          </div>
        </div>

        <template v-if="sidePanelTab === 'input'">
          <div class="flex flex-col py-2">
            <h5 class="py-3">Signature Fields</h5>

            <div
              class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
              @click="addField('signature')"
            >
              <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
              <p class="text-brand-green">Signature</p>
            </div>

            <div
              class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
              @click="addField('initial')"
            >
              <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
              <p class="text-brand-green">Initial</p>
            </div>

            <h5 class="py-3">Input fields</h5>

            <div
              class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
              @click="addField('checkbox')"
            >
              <img src="@/assets/icons/checkbox.svg" class="w-5 h-5 text-brand-green mr-2" />
              <p class="text-brand-green">Checkbox</p>
            </div>

            <div
              class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
              @click="addField('text')"
            >
              <div class="flex items-center mr-2">
                <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
              </div>
              <div class="flex items-center">
                <p class="text-brand-green">Text Input</p>
              </div>
            </div>

            <div
              class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
              @click="addField('date')"
            >
              <CalendarIcon class="w-5 h-5 text-brand-green mr-2" />
              <div class="flex items-center">
                <p class="text-brand-green">Date Input</p>
              </div>
            </div>

            <h5 class="py-3">Display fields</h5>

            <div
              class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
              @click="addField('text_display')"
            >
              <div class="flex items-center mr-2">
                <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
              </div>
              <p class="text-brand-green">Text Display</p>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col py-2 px-3">
            <div class="flex items-center pb-5">
              <FormDropdown
                :initial="linkedForm"
                @select="formSelect"
                :placeholder="'Select a form to link'"
              />
            </div>

            <template v-if="isLoadingMappableFields">
              <div class="flex items-center py-3 mx-auto">
                <SmallLoading />
              </div>
            </template>
            <template v-else>
              <template v-if="linkedForm">
                <div
                  class="flex flex-col py-2"
                  v-for="section in linkedForm.form.sections"
                  :key="section"
                >
                  <p class="py-1 text-lg">{{ section.label }}</p>

                  <div
                    class="flex items-center border rounded mb-3 px-5 py-2 cursor-pointer"
                    @click="addMappableField(field)"
                    v-for="field in section.fields"
                    :key="field.name"
                  >
                    <p class="text-brand-green truncate">{{ field.label }}</p>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </template>
      </div>
      <!-- END LEFT PANEL -->

      <!-- START DOCUMENT VIEW -->
      <div class="flex w-full md:w-2/3 bg-white panel rounded h-screen py-5 px-5" v-if="overlay">
        <template v-if="!isAllImageLoaded">
          <div
            id="modal-bg"
            class="flex items-center justify-center w-full h-screen bg-black opacity-20 top-0 left-0 fixed z-[15]"
          >
            <Loading class="opacity-100" />
          </div>
        </template>
        <div class="flex flex-col w-full h-full relative overflow-y-scroll" ref="file-frame">
          <div
            v-for="(file, idx) in getCurrentDocumentTemplate.attachments"
            :key="idx"
            class="relative"
            ref="file-render"
            :id="`${file.name}`"
            @mousemove="trackCursor"
          >
            <div class="flex items-center parent">
              <template v-if="file.name.split('.').pop() === 'pdf'">
                <VuePdfEmbed
                  :source="file.url"
                  :width="overlay.frameWidth"
                  @rendered="filesLoaded[file.name] = true"
                  :disableAnnotationLayer="true"
                />
              </template>
              <template v-else>
                <img
                  :src="file.url"
                  class="w-full"
                  :ref="`rendered-${file.name}`"
                  :style="{
                    'max-width': 'none',
                    width: `${overlay.frameWidth}px`
                  }"
                  @load="filesLoaded[file.name] = true"
                />
              </template>
              <template
                v-if="overlay[file.name] && overlay[file.name].fields && filesLoaded[file.name]"
              >
                <DocumentDraggableField
                  :id="`draggable-${file.name}-${mapperData.x}-${mapperData.y}`"
                  v-for="(mapperData, idx) in overlay[file.name].fields"
                  :key="`draggable-${file.name}-${mapperData.x}-${mapperData.y}`"
                  :field="mapperData"
                  @drag-start="dragStart"
                  @drag-end="dragEnd($event, file.name, idx)"
                  @resize-end="resizeEnd($event, file.name, idx)"
                  @set-value="mapperData.label = $event"
                  @activated="toggleActivated(mapperData)"
                  @remove="remove(file.name, idx)"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- END DOCUMENT VIEW -->
    </div>
  </template>

  <!-- START RIGHT SIDE PANEL -->
  <FieldSidePanel
    :activeField="activeField"
    @close="toggleActivated"
    @update="handleFieldUpdate"
    @duplicate="handleFieldDuplicate"
  />
  <!-- <template v-if="activeField">
    <div class="fixed right-0 top-0 w-60 h-screen bg-white shadow py-5 px-5 z-20">
      <div class="flex w-full items-center justify-end mb-5">
        <XIcon
          class="text-brand-dark-gray w-7 h-7 cursor-pointer"
          @click="toggleActivated()"
          ref="close"
        />
      </div>
      <div
        class="flex items-center border rounded mb-5 px-5 py-2"
        v-if="activeField.type === 'text_display'"
      >
        <div class="flex items-center mr-2">
          <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
        </div>
        <p class="text-brand-green">Text Display</p>
      </div>

      <div
        class="flex items-center border rounded mb-5 px-5 py-2"
        v-if="activeField.type === 'initial'"
      >
        <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
        <p class="text-brand-green">Initial</p>
      </div>

      <div
        class="flex items-center border rounded mb-5 px-5 py-2"
        v-if="activeField.type === 'signature'"
      >
        <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
        <p class="text-brand-green">Signature</p>
      </div>

      <div
        class="flex items-center border rounded mb-5 px-5 py-2"
        v-if="activeField.type === 'checkbox'"
      >
        <img src="@/assets/icons/checkbox.svg" class="w-5 h-5 text-brand-green mr-2" />
        <p class="text-brand-green">Checkbox</p>
      </div>

      <div
        class="flex items-center border rounded mb-5 px-5 py-2"
        v-if="activeField.type === 'text'"
      >
        <div class="flex items-center mr-2">
          <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
        </div>
        <div class="flex items-center">
          <p class="text-brand-green">Text Input</p>
        </div>
      </div>

      <div
        class="flex items-center border rounded mb-5 px-5 py-2"
        v-if="activeField.type === 'date'"
      >
        <CalendarIcon class="w-5 h-5 text-brand-green mr-2" />
        <div class="flex items-center">
          <p class="text-brand-green">Date Input</p>
        </div>
      </div>

      <div class="flex items-center px-1">
        <input
          id="field-required"
          type="checkbox"
          class="rounded border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="activeField.required"
        />
        <label for="field-required" class="ml-2 block text-brand-black">Required? </label>
      </div>

      <template v-if="['signature', 'initial'].includes(activeField.type)">
        <div class="flex items-center my-5">
          <DropDown
            :options="signeeOptions"
            placeholder="Signee type"
            :initial="
              activeField.user_type
                ? signeeOptions.find((o) => o.value === activeField.user_type)
                : null
            "
            @select="activeField.user_type = $event.value"
          ></DropDown>
        </div>
      </template>
    </div>
  </template> -->
  <!-- END RIGHT SIDE PANEL -->
</template>
<script>
import _ from "lodash"
import { v4 as uuidv4 } from "uuid"

import { mapGetters } from "vuex"
import {
  DOCUMENT_TEMPLATE_GET,
  DOCUMENT_TEMPLATE_UPDATE,
  APPLICATION_TEMPLATE_GET
} from "@/store/types"

import VuePdfEmbed from "vue-pdf-embed"
import { PencilIcon, CalendarIcon, ChevronLeftIcon } from "@heroicons/vue/outline"

import fieldDefaults from "@/constants/documentFieldDefaults"
import FormDropdown from "../forms/components/FormDropdown.vue"
import FieldSidePanel from "./components/FieldSidePanel.vue"

export default {
  components: {
    PencilIcon,
    CalendarIcon,
    ChevronLeftIcon,
    VuePdfEmbed,
    FormDropdown,
    FieldSidePanel
  },
  computed: {
    ...mapGetters(["getCurrentDocumentTemplate", "isDocumentTemplateLoading"]),
    isAllImageLoaded() {
      if (!this.overlay) {
        return false
      }

      const loaded = Object.keys(this.filesLoaded)
        .map((key) => {
          return this.filesLoaded[key]
        })
        .filter(Boolean)
      return loaded.length === Object.keys(this.getCurrentDocumentTemplate.attachments).length
    }
  },
  data() {
    return {
      filesLoaded: {},
      overlay: null,
      fieldDefaults,
      name: "",
      description: "",
      fileFrameWidth: 0,
      activeField: null,
      id: "",
      cursorPosition: {
        x: 0,
        y: 0
      },
      isDragging: false,
      signeeOptions: [
        {
          label: "Applicant",
          value: "applicant"
        },
        {
          label: "Landlord",
          value: "landlord"
        },
        {
          label: "Agent",
          value: "agent"
        },
        {
          label: "Other",
          value: "other"
        }
      ],
      sidePanelTab: "input",
      linkedForm: null,
      isLoadingMappableFields: false
    }
  },
  async mounted() {
    const { id } = this.$route.params
    this.id = id

    await this.loadTemplate()
    this.overlay = _.cloneDeep(this.getCurrentDocumentTemplate.overlay)

    if (this.getCurrentDocumentTemplate.linked_form) {
      const response = await this.$store.dispatch(APPLICATION_TEMPLATE_GET, {
        slug: this.getCurrentDocumentTemplate.linked_form
      })
      this.linkedForm = response.data
    }

    this.$nextTick(() => {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio <= 0) return

        this.fileVisible = entries[0].target.id
      })

      this.$refs["file-render"].forEach((el) => {
        this.observer.observe(el)
      })
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.$refs["file-render"].forEach((el) => {
        this.observer.unobserve(el)
      })
    }
  },
  methods: {
    addMappableField(field) {
      const fieldData = _.cloneDeep(this.fieldDefaults.find((d) => d.type === "mappable"))
      fieldData.field_name = field.name
      fieldData.field_label = field.label

      fieldData.x = this.cursorPosition.x
      fieldData.y = this.cursorPosition.y

      this.overlay[this.fileVisible].fields.push(fieldData)
    },
    async formSelect(form) {
      if (this.linkedForm && this.linkedForm.slug === form.slug) {
        console.log("same form!!!")
        return
      }

      this.isLoadingMappableFields = true
      try {
        const response = await this.$store.dispatch(APPLICATION_TEMPLATE_GET, { slug: form.slug })
        this.linkedForm = response.data

        Object.keys(this.overlay).forEach((key) => {
          if (this.overlay[key].fields) {
            this.overlay[key].fields = this.overlay[key].fields.filter((f) => f.type !== "mappable")
          }
        })
      } catch (err) {
        console.log(err)
      }
      this.isLoadingMappableFields = false
    },
    setSidePanelTab(tab) {
      this.sidePanelTab = tab
    },
    navigateBack() {
      this.$router.go(-1)
    },
    async loadTemplate() {
      const { id } = this
      try {
        await this.$store.dispatch(DOCUMENT_TEMPLATE_GET, { id })
        this.getCurrentDocumentTemplate.attachments.forEach((at) => {
          this.filesLoaded[at.name] = false
        })

        this.name = this.getCurrentDocumentTemplate.name
      } catch (err) {
        console.log(err)
      }
    },
    toggleActivated(field) {
      if (!field || (field && field.type !== "text_display")) {
        this.activeField = field
      }
    },
    dragStart() {
      this.isDragging = true
    },
    dragEnd(positionalData, filename, idx) {
      this.isDragging = false
      this.overlay[filename].fields[idx].x = positionalData.x
      this.overlay[filename].fields[idx].y = positionalData.y

      if (this.activeField) {
        this.activeField = null
      }
    },
    resizeEnd(positionalData, filename, idx) {
      this.overlay[filename].fields[idx].w = positionalData.w
      this.overlay[filename].fields[idx].h = positionalData.h
    },
    remove(filename, idx) {
      this.overlay[filename].fields = [
        ...this.overlay[filename].fields.filter((f, index) => index !== idx)
      ]

      this.activeField = null
    },
    addField(type) {
      const fieldData = _.cloneDeep(this.fieldDefaults.find((d) => d.type === type))

      fieldData.x = this.cursorPosition.x
      fieldData.y = this.cursorPosition.y
      fieldData.id = uuidv4()

      this.overlay[this.fileVisible].fields.push(fieldData)
    },
    trackCursor(e) {
      const bounds = document.getElementById(this.fileVisible).getBoundingClientRect()

      this.cursorPosition.x = e.clientX - bounds.left
      this.cursorPosition.y = e.clientY - bounds.top
    },
    async save() {
      const { overlay, id, name } = this

      const linkedForm = this.linkedForm ? this.linkedForm.slug : null

      //Sort the overlay to be ordered by y index then x
      this.getCurrentDocumentTemplate.attachments.forEach((at) => {
        overlay[at.name].fields = _.orderBy(overlay[at.name].fields, ["y", "x"], ["asc", "asc"])
      })

      try {
        await this.$store.dispatch(DOCUMENT_TEMPLATE_UPDATE, {
          id,
          overlay,
          name,
          linkedForm
        })
        this.$router.push({ name: "dashboard_documents" })
      } catch (err) {
        console.log(err)
      }
    },
    handleFieldUpdate(updates) {
      Object.keys(updates).map((field) => {
        this.activeField[field] = updates[field]
      })
    },
    handleFieldDuplicate() {
      const fieldData = _.cloneDeep(
        this.fieldDefaults.find((d) => d.type === this.activeField.type)
      )

      Object.keys(this.activeField).map((field) => {
        fieldData[field] = this.activeField[field]
      })

      fieldData.y = fieldData.y + 75
      fieldData.id = uuidv4()

      this.overlay[this.fileVisible].fields.push(fieldData)
    }
  }
}
</script>
<style scoped>
.panel {
  max-height: 80vh;
  overflow-y: scroll;
}
@media (max-width: 768px) {
  .panel {
    max-height: 100vh;
  }
}
</style>
